// vim: set ts=2 sts=2 sw=2 et:

// Sala live en memoria para el lab: reemplaza window.WebSocket mientras la página está montada, así las
// pantallas reales (espectador, streaming) se conectan como siempre y reciben el estado de la escena elegida.

import { selectPlates } from "../../logic/barLoad";
import type { MeetState } from "../../types/stateTypes";
import type {
  JudgeLights,
  PlatformLiveAttempt,
  PlatformLiveBroadcastInfo,
  PlatformLiveServerMessage,
  PlatformLiveState,
} from "../live/platformLiveProtocol";
import type { LabScenario } from "./labScenarios";

const REVEAL_HOLD_MS = 60 * 60 * 1000;

let currentState: PlatformLiveState | null = null;
const sockets = new Set<LabWebSocket>();

class LabWebSocket {
  readyState = 0;
  onopen: ((event: unknown) => void) | null = null;
  onmessage: ((event: { data: string }) => void) | null = null;
  onclose: ((event: unknown) => void) | null = null;

  constructor(readonly url: string) {
    sockets.add(this);
    window.setTimeout(() => {
      if (!sockets.has(this)) return;
      this.readyState = 1;
      this.onopen?.({});
    }, 0);
  }

  send(data: string) {
    const message = JSON.parse(data) as { type?: string };
    if (message.type === "spectatorJoin" && currentState !== null) {
      this.deliver({ type: "accepted", role: "spectator", state: currentState });
    }
  }

  // Sin onclose: la pantalla solo cierra al desmontarse, y un onclose dispararía su reconexión.
  close() {
    this.readyState = 3;
    sockets.delete(this);
  }

  deliver(message: PlatformLiveServerMessage) {
    this.onmessage?.({ data: JSON.stringify(message) });
  }
}

export const installLabWebSocket = (): (() => void) => {
  const original = window.WebSocket;
  (window as unknown as { WebSocket: unknown }).WebSocket = LabWebSocket;
  return () => {
    window.WebSocket = original;
    sockets.clear();
    currentState = null;
  };
};

export const publishLabState = (state: PlatformLiveState) => {
  currentState = state;
  sockets.forEach((socket) => {
    if (socket.readyState === 1) socket.deliver({ type: "platformState", state });
  });
};

const offLights = (): JudgeLights => ({ J1: "off", J2: "off", J3: "off" });

// La barra cargada como la calcula el operador, con las pesas del meet por defecto.
export const withBarLoad = (attempt: PlatformLiveAttempt, meet: MeetState): PlatformLiveAttempt => {
  const barKg =
    attempt.lift === "S"
      ? meet.squatBarAndCollarsWeightKg
      : attempt.lift === "B"
        ? meet.benchBarAndCollarsWeightKg
        : meet.deadliftBarAndCollarsWeightKg;
  return {
    ...attempt,
    barLoad: { loading: selectPlates(attempt.weightKg, barKg, meet.plates, meet.inKg), inKg: meet.inKg, rackInfo: "" },
  };
};

export const buildLabLiveState = (
  scenario: LabScenario,
  nextAttempt: PlatformLiveAttempt | null,
  meet: MeetState,
  nowMs: number,
  broadcast?: PlatformLiveBroadcastInfo,
): PlatformLiveState => {
  // Como el operador: los datos del torneo viajan con el intento actual.
  const attempt =
    scenario.attempt === null
      ? null
      : { ...withBarLoad(scenario.attempt, meet), ...(broadcast === undefined ? {} : { broadcast }) };
  const revealing = attempt !== null && scenario.reveal !== null;
  const lastResolvedAttempt = revealing
    ? {
        attemptKey: attempt.key,
        attempt,
        source: "automatic" as const,
        result: scenario.reveal as "good" | "noLift",
        resolvedAtMs: nowMs,
        revealUntilMs: nowMs + REVEAL_HOLD_MS,
        judgeLights: scenario.judgeLights,
      }
    : scenario.waitingForNext === true
      ? {
          attemptKey: "lab:espera",
          attempt: null,
          source: "automatic" as const,
          result: "good" as const,
          resolvedAtMs: nowMs - 10_000,
          revealUntilMs: nowMs - 5_000,
        }
      : null;
  const clockRunning = attempt !== null && !revealing && scenario.clockSeconds !== null;
  return {
    operatorConnected: true,
    serverNowMs: nowMs,
    currentAttempt: revealing ? null : attempt,
    nextAttempt: nextAttempt === null ? null : withBarLoad(nextAttempt, meet),
    lastResolvedAttempt,
    attemptClock:
      clockRunning && attempt !== null
        ? {
            attemptKey: attempt.key,
            startedAtMs: nowMs - (60 - (scenario.clockSeconds as number)) * 1000,
            endsAtMs: nowMs + (scenario.clockSeconds as number) * 1000,
            durationMs: 60_000,
          }
        : null,
    judgeLights: revealing ? scenario.judgeLights : offLights(),
    votes: {},
    closedAttemptKey: null,
    features: { judgeVoting: true, spectatorDisplay: true, dmx: false, manualVerdictConfirmation: false },
    dmx: { status: "disabled" },
  };
};
